import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiCalendar, FiChevronLeft, FiChevronRight, FiClock, FiInfo } = FiIcons;

const WorkshopCalendar = ({ workshops, onSelectWorkshop, selectedDate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  
  const workshopDates = workshops.reduce((acc, w) => {
    const d = new Date(w.date);
    if (d.getMonth() === currentMonth.getMonth() && d.getFullYear() === currentMonth.getFullYear()) {
      acc[d.getDate()] = w;
    }
    return acc;
  }, {});

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)));

  return (
    <div className="bg-white rounded-[40px] border border-brand-dark p-6 md:p-8 shadow-sm">
      <div className="flex items-center justify-between mb-8">
        <h4 className="font-serif text-xl text-accent-navy">
          {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
        </h4>
        <div className="flex gap-2">
          <button onClick={prevMonth} className="p-2 hover:bg-brand-light rounded-full text-text-muted transition-colors">
            <SafeIcon icon={FiChevronLeft} />
          </button>
          <button onClick={nextMonth} className="p-2 hover:bg-brand-light rounded-full text-text-muted transition-colors">
            <SafeIcon icon={FiChevronRight} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-6">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
          <div key={day} className="text-center text-[10px] font-black text-text-muted uppercase py-2">
            {day}
          </div>
        ))}
        
        {Array.from({ length: firstDayOfMonth }).map((_, i) => (
          <div key={`empty-${i}`} className="aspect-square" />
        ))}

        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const workshop = workshopDates[day];
          const isSelected = selectedDate && new Date(selectedDate).getDate() === day;
          
          return (
            <button 
              key={day}
              onClick={() => workshop && onSelectWorkshop(workshop)}
              className={`relative aspect-square flex items-center justify-center text-xs rounded-xl transition-all ${
                workshop 
                  ? 'bg-accent-gold text-white font-bold shadow-lg shadow-accent-gold/20 scale-105 z-10' 
                  : 'text-text-muted hover:bg-brand-light'
              } ${isSelected ? 'ring-2 ring-accent-navy ring-offset-2' : ''}`}
            >
              {day}
              {workshop && (
                <motion.div 
                  layoutId="active-dot"
                  className="absolute bottom-1 w-1 h-1 bg-white rounded-full"
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="pt-6 border-t border-brand-light space-y-4">
        <h5 className="text-[10px] font-black uppercase tracking-widest text-accent-navy flex items-center justify-between">
          Monthly Schedule
          <SafeIcon icon={FiInfo} className="text-accent-gold w-3 h-3" />
        </h5>
        
        <div className="space-y-3 max-h-[200px] overflow-y-auto pr-2 custom-scrollbar">
          {Object.entries(workshopDates).length > 0 ? (
            Object.entries(workshopDates).map(([day, w]) => (
              <button 
                key={w.id}
                onClick={() => onSelectWorkshop(w)}
                className="w-full text-left group transition-all"
              >
                <div className="flex items-start gap-3 p-2 rounded-xl hover:bg-brand-light">
                  <div className="w-10 h-10 rounded-xl bg-brand-light flex items-center justify-center text-accent-gold group-hover:bg-accent-gold group-hover:text-white transition-all shrink-0">
                    <span className="text-xs font-black">{day}</span>
                  </div>
                  <div className="flex-grow min-w-0">
                    <p className="text-xs font-bold text-accent-navy group-hover:text-accent-gold transition-colors line-clamp-1 truncate">
                      {w.title}
                    </p>
                    <div className="flex items-center gap-2 text-[10px] text-text-muted mt-0.5">
                      <SafeIcon icon={FiClock} className="w-3 h-3" />
                      <span>{w.time}</span>
                    </div>
                  </div>
                </div>
              </button>
            ))
          ) : (
            <p className="text-[10px] text-text-muted italic py-4 text-center">No workshops scheduled for this month.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkshopCalendar;